import type { HeroId } from '../core';
import { PAINTED_POSES } from './cast';
import { rasterUrl } from './raster';

export interface WingSide {
  heroId: HeroId | null;
  pose?: 'idle' | 'cheer' | 'dismay' | 'boast';
  kicker: string;
  title: string;
  lines: readonly string[];
}

const STYLE_ID = 'cc-wing-style';

let leftEl: HTMLElement | null = null;
let rightEl: HTMLElement | null = null;
let observer: ResizeObserver | null = null;

/** Side panels for wide windows. The 720-wide column stays where it is. */
export function showWings(left: WingSide, right: WingSide): void {
  const root = ensure();
  paint(root.left, left);
  paint(root.right, right);
  layoutWings();
}

function ensure(): { left: HTMLElement; right: HTMLElement } {
  if (leftEl && rightEl) return { left: leftEl, right: rightEl };
  if (typeof document === 'undefined') {
    const dummy = { style: {}, append: () => {}, querySelector: () => null } as unknown as HTMLElement;
    leftEl = dummy;
    rightEl = dummy;
    return { left: dummy, right: dummy };
  }
  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    document.head.appendChild(style);
  }
  const host = document.createElement('div');
  host.id = 'cc-wings';
  host.innerHTML =
    '<aside class="wing left"></aside><aside class="wing right"></aside>';
  document.body.appendChild(host);
  leftEl = host.querySelector('.left');
  rightEl = host.querySelector('.right');
  if (!leftEl || !rightEl) throw new Error('wings missing');
  const watch = () => layoutWings();
  window.addEventListener('resize', watch);
  const canvas = document.querySelector('#game canvas');
  if (canvas && typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(watch);
    observer.observe(canvas);
  }
  return { left: leftEl, right: rightEl };
}

export function layoutWings(): void {
  if (!leftEl || !rightEl || typeof document === 'undefined') return;
  const canvas = document.querySelector('#game canvas');
  if (!canvas) {
    leftEl.style.display = 'none';
    rightEl.style.display = 'none';
    return;
  }
  const rect = canvas.getBoundingClientRect();
  const leftGap = Math.max(0, rect.left);
  const rightGap = Math.max(0, window.innerWidth - rect.right);
  const show = leftGap >= 200 && rightGap >= 200;
  place(leftEl, show, 0, leftGap);
  place(rightEl, show, rect.right, rightGap);
}

function place(el: HTMLElement, show: boolean, x: number, width: number): void {
  el.style.display = show ? 'flex' : 'none';
  el.style.left = `${Math.round(x)}px`;
  el.style.width = `${Math.round(width)}px`;
}

function paint(el: HTMLElement, side: WingSide): void {
  if (typeof document === 'undefined' || typeof el.replaceChildren !== 'function') return;
  let img = el.querySelector<HTMLImageElement>('img.wing-hero');
  let card = el.querySelector<HTMLElement>('.wing-card');
  if (!img || !card) {
    el.replaceChildren();
    img = document.createElement('img');
    img.className = 'wing-hero';
    img.alt = '';
    card = document.createElement('div');
    card.className = 'wing-card';
    const kicker = document.createElement('p');
    kicker.className = 'wing-kicker';
    const title = document.createElement('p');
    title.className = 'wing-title';
    const list = document.createElement('ul');
    list.className = 'wing-lines';
    card.append(kicker, title, list);
    el.append(img, card);
  }
  const next = side.heroId ? heroSrc(side.heroId, side.pose ?? 'idle') : '';
  if (img.getAttribute('src') !== next) img.src = next;
  img.style.display = side.heroId ? 'block' : 'none';
  const kicker = card.querySelector('.wing-kicker');
  const title = card.querySelector('.wing-title');
  const list = card.querySelector('.wing-lines');
  if (kicker) kicker.textContent = side.kicker;
  if (title) title.textContent = side.title;
  if (list) {
    const lines = [...list.children];
    while (list.children.length > side.lines.length) list.lastElementChild?.remove();
    side.lines.forEach((line, index) => {
      const item = lines[index] ?? document.createElement('li');
      item.textContent = line;
      if (!item.parentElement) list.appendChild(item);
    });
  }
}

function heroSrc(id: HeroId, pose: 'idle' | 'cheer' | 'dismay' | 'boast'): string {
  const art = PAINTED_POSES.has(id) ? pose : 'idle';
  return rasterUrl(`/assets/heroes/cc_hero_${id}_${art}_full@1x.png`);
}

const CSS = `
#cc-wings { position: fixed; inset: 0; pointer-events: none; z-index: 4; }
.wing {
  position: absolute;
  top: 0;
  bottom: 0;
  display: none;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px 18px 32px;
  box-sizing: border-box;
}
.wing.left { box-shadow: inset -8px 0 0 #5c3418, inset -14px 0 0 #e7c98a; }
.wing.right { box-shadow: inset 8px 0 0 #5c3418, inset 14px 0 0 #e7c98a; }
.wing-hero {
  width: auto;
  max-width: 86%;
  height: min(42vh, 380px);
  object-fit: contain;
  filter: drop-shadow(0 10px 0 rgba(42, 28, 18, 0.2));
}
.wing-card {
  width: min(280px, 92%);
  margin: 0;
  padding: 18px 16px 16px;
  color: #FFF6E4;
  font-family: Fredoka, sans-serif;
  font-weight: 700;
  text-align: center;
  text-shadow: 0 2px 0 #3B2414, 0 0 2px #3B2414;
  border: 36px solid transparent;
  border-image: url("/assets/ui/cc_ui_panel_wood@1x.png") 50 92 56 72 fill;
  box-sizing: border-box;
}
.wing-kicker {
  margin: 0;
  font-size: 15px;
  letter-spacing: 0.04em;
  opacity: 0.8;
}
.wing-title {
  margin: 2px 0 0;
  font-size: 28px;
  line-height: 1.1;
}
.wing-lines {
  margin: 8px 0 0;
  padding: 0;
  list-style: none;
  font-size: 18px;
  line-height: 1.35;
}
.wing-lines li + li { margin-top: 4px; }
`;
